import { Injectable, inject } from '@angular/core';
import { AuthService } from './auth/auth.service';

interface CacheEntry<T> {
    promise: Promise<T>;
    timestamp: number;
    username: string | null;
    organisation: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class PromiseCache {

    private readonly authService = inject(AuthService);
    private entries = new Map<string, CacheEntry<unknown>>();

    getOrCreate<T>(key: string, ttlMs: number, loader: () => Promise<T>): Promise<T> {
        const now = Date.now();
        const existing = this.entries.get(key) as CacheEntry<T> | undefined;
        const username = this.authService.currentUser() ?? null;
        const organisation = this.authService.currentOrganisation() ?? null;

        if (existing && (now - existing.timestamp) < ttlMs) {
            if (existing.username !== username || existing.organisation !== organisation) {
                this.entries.delete(key);
            } else {
                return existing.promise;
            }
        }

        const promise = loader().catch(err => {
            const current = this.entries.get(key);
            if (current && current.promise === promise) {
                this.entries.delete(key);
            }
            throw err;
        });

        this.entries.set(key, { promise, timestamp: now, username, organisation });
        return promise;
    }

    clear(key?: string) {
        if (key) {
            this.entries.delete(key);
        } else {
            this.entries.clear();
        }
    }
}
